import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { ApiError } from '@/lib/apiClient';

import {
  type Plan,
  type PlanDraft,
  archivePlan,
  createPlan,
  getPlan,
  listPlans,
  publishPlan,
  savePlan,
} from './api';

export const PLANS_KEY = ['plans'] as const;

const retry = (failureCount: number, error: unknown) => {
  if (error instanceof ApiError && error.status < 500) return false;
  return failureCount < 2;
};

export function usePlans(relationshipId: string) {
  return useQuery({
    queryKey: [...PLANS_KEY, 'of', relationshipId],
    queryFn: () => listPlans(relationshipId),
    retry,
  });
}

export function usePlan(planId: string) {
  return useQuery({
    queryKey: [...PLANS_KEY, 'item', planId],
    queryFn: () => getPlan(planId),
    retry,
    // O editor guarda a agenda em estado local enquanto a pessoa monta.
    // Um refetch em segundo plano trocaria o que está debaixo dos dedos dela.
    refetchOnWindowFocus: false,
    staleTime: Infinity,
  });
}

function usePlanMutation<TVars>(fn: (vars: TVars) => Promise<Plan>) {
  const queryClient = useQueryClient();
  return useMutation<Plan, Error, TVars>({
    mutationFn: fn,
    onSuccess: (plano) => {
      queryClient.setQueryData([...PLANS_KEY, 'item', plano.plan_id], plano);
      void queryClient.invalidateQueries({
        queryKey: PLANS_KEY,
        predicate: (consulta) => consulta.queryKey[1] === 'of',
      });
    },
  });
}

export function useCreatePlan() {
  return usePlanMutation(
    ({ relationshipId, draft }: { relationshipId: string; draft: PlanDraft }) =>
      createPlan(relationshipId, draft),
  );
}

export function useSavePlan() {
  return usePlanMutation(
    ({ planId, draft, version }: { planId: string; draft: PlanDraft; version: number }) =>
      savePlan(planId, draft, version),
  );
}

export function usePublishPlan() {
  return usePlanMutation(({ planId, version }: { planId: string; version: number }) =>
    publishPlan(planId, version),
  );
}

export function useArchivePlan() {
  return usePlanMutation(({ planId, version }: { planId: string; version: number }) =>
    archivePlan(planId, version),
  );
}

/**
 * O que falta para publicar, como o servidor respondeu.
 *
 * Vem em `details.blockers`, todos de uma vez — descobri-los um a um, cada um
 * depois de uma tentativa, transformaria publicar em adivinhação.
 */
export function publicationBlockers(error: unknown): string[] {
  if (error instanceof ApiError && Array.isArray(error.details.blockers)) {
    return error.details.blockers.filter((item): item is string => typeof item === 'string');
  }
  return [];
}

export function planErrorKey(error: unknown): string {
  if (error instanceof ApiError) {
    if (error.code === 'version_conflict') return 'plans:error.conflict';
    if (error.code === 'forbidden') return 'plans:error.forbidden';
    const rule = error.details.rule;
    if (typeof rule === 'string') return `plans:rule.${rule}`;
  }
  return 'plans:error.unknown';
}
